import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Phone, MapPin, ArrowRight } from "lucide-react";
import { useContent } from "../ContentContext";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};
const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" variants={line} custom={i} initial="hidden" animate="show">{children}</motion.span>
  </span>
);

export default function Hero({ onEstimate, onSchedule }) {
  const { company } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen w-full overflow-hidden noise"
             style={{ background: "var(--pine)" }}>
      <motion.div className="absolute inset-0" style={{ scale, y: yImg }}>
        <img src={company.hero_image} alt="Professional arborist climbing and removing a large tree in Huntsville Alabama"
             className="w-full h-full object-cover" style={{ opacity: 0.4 }} />
      </motion.div>
      <div className="absolute inset-0"
           style={{ background: "linear-gradient(to top, rgba(14,61,34,0.96), rgba(14,61,34,0.45) 55%, rgba(14,61,34,0.6))" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pb-16 pt-28">
        {/* Logo as primary branding */}
        {company.logo && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                      className="mb-6 inline-block self-start">
            <span className="hero-logo-wrap">
              <img src={company.logo} alt={`${company.name} logo`} data-testid="hero-logo"
                   className="h-24 sm:h-32 w-auto block" />
            </span>
          </motion.div>
        )}

        {/* 24/7 emergency + trust badges */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="flex flex-wrap items-center gap-3 mb-6">
          {company.emergency_247 && (
            <a href={`tel:${company.phoneRaw}`} data-testid="hero-emergency-badge"
               className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold animate-pulse"
               style={{ background: "#C0261F", color: "#fff" }}>
              <Phone size={15} /> 24/7 Emergency — Call Now
            </a>
          )}
          {company.licensed_insured && (
            <span className="inline-flex items-center gap-2 text-[12px] font-semibold tracking-[0.15em] uppercase" style={{ color: "var(--sun)" }}>
              <ShieldCheck size={15} /> Licensed &amp; Insured
            </span>
          )}
          <span className="inline-flex items-center gap-2 text-[12px] font-semibold tracking-[0.15em] uppercase" style={{ color: "var(--moss)" }}>
            <MapPin size={15} style={{ color: "var(--brass)" }} /> Huntsville · North Alabama · Tennessee
          </span>
        </motion.div>

        {/* Company name = main focus */}
        <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tight text-5xl sm:text-6xl lg:text-7xl"
            style={{ color: "var(--moss)" }} data-testid="hero-title">
          <Line i={0}>Top of the Line</Line>
          <Line i={1}><span style={{ color: "var(--sun)" }}>Tree Services</span> LLC</Line>
        </h1>

        <p className="mt-4 font-serif-a italic text-2xl sm:text-3xl" style={{ color: "var(--moss)" }}>
          {company.tagline}
        </p>
        <p className="mt-3 max-w-xl text-base sm:text-lg leading-relaxed" style={{ color: "rgba(241,237,227,0.9)" }}>
          {company.sub}
        </p>

        {/* CTAs — big eye-catching yellow */}
        <div className="mt-7 flex flex-col sm:flex-row gap-4">
          <button data-testid="hero-estimate-btn" onClick={onEstimate}
                  className="group inline-flex items-center justify-center gap-3 rounded-full px-10 py-5 text-lg font-extrabold uppercase tracking-wide shadow-2xl transition-transform duration-300 hover:scale-[1.04]"
                  style={{ background: "var(--sun)", color: "var(--pine)", boxShadow: "0 10px 40px rgba(244,206,10,0.4)" }}>
            Request a Free Estimate
            <ArrowRight size={22} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button data-testid="hero-schedule-btn" onClick={onSchedule}
                  className="rounded-full px-8 py-5 font-semibold border-2 transition-colors hover:bg-white/10"
                  style={{ borderColor: "rgba(241,237,227,0.5)", color: "var(--moss)" }}>
            Schedule Your Service
          </button>
        </div>

        <div className="mt-10 flex items-center gap-4">
          <span className="font-display text-4xl font-extrabold" style={{ color: "var(--sun)" }}>{company.years}</span>
          <span className="text-sm max-w-[180px]" style={{ color: "rgba(241,237,227,0.7)" }}>
            years of experience serving our community
          </span>
        </div>
      </div>
    </section>
  );
}
